import React from 'react';
import {
    DateInput,
    Select,
    CheckBox,
    InputNumber,
    SearchableDropdown
} from '../../Common';
import {
    Grid,
    Typography,
    Paper,
    Chip,
    Divider,
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    RadioGroup,
    Radio,
} from '@mui/material';
import { trakrefBlue, Grey1, trakrefYellow } from "../../../color_utils";
import Enums from "../../../enums";
import UserInfo from "../../../UserInfo";
import { renderWeight } from "../../../Utils/weight_utils";
import { formatDateWithZ } from '../../../utils';

const weightPrecision = UserInfo.getInstance(UserInfo.getInstanceID()) ? UserInfo.getInstance(UserInfo.getInstanceID()).weightPrecision : null


const paperProps = { pb: 3, px: 2, borderRadius: "15px" };
const paperResponsiveStyle = { ...paperProps };
const radioStyle = {
    color: "#9e9e9e",
    "&.Mui-checked": {
        color: trakrefBlue,
    },
};

const getChipData = (asset) => {
    const {
        coolingAppliance: { coolingApplianceStatusId = null } = {},
        isChecked,
        workItemDetail: { wasLeakFound } = {}
    } = asset;
    let chipData = {
        chipLabel: "No Change",
        color: "#FFF",
        chipColor: trakrefBlue,
        disabled: false
    };
    
    if (coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.UnderRepair) {
        chipData = {
            chipLabel: "Under Repair",
            chipColor: trakrefYellow,
            disabled: true
        };
    }
    
    if (isChecked && [null, undefined].includes(wasLeakFound)) {
        chipData.chipLabel = "Pending";
    }

    if (isChecked && wasLeakFound === false) {
        chipData.chipLabel = "No Leak Found";
    }

    if (isChecked && wasLeakFound === true) {
        chipData.chipLabel = "Leak Found";
    }
    
    return chipData;
};

const getAssetsClone = (assets) => [...assets];

const SystemSelect = ({ index, asset, assets, setAssets, dropDownOptions, loading, topLevelInspectionDate }) => {
    const {
        isChecked = false,
        name = '',
        tagNumber = '',
        serialNumber = '',
        installDate = '',
        assetType: {
            name: assetTypeName = '',
        },
        coolingAppliance: {
            gasChargeCapacityLbs = null,
            materialType: { name: materialTypeName = '' },
        } = {},
        workItemDetail: {
            wasLeakFound = null,
            inspectionDate = null,
            leakLocationId = '',
            leakSublocationId = '',
            faultCauseTypeId = '',
            estimatedLeakAmount = '',
        } = {},
    } = asset;
    const { chipLabel,  chipColor, disabled } = getChipData(asset);
    
    const inventoryData = [
        { label: "System Type", value: assetTypeName },
        { label: "Tag Number", value: tagNumber },
        { label: "Gas Capacity", value: renderWeight(gasChargeCapacityLbs, weightPrecision || 3, weightPrecision || 2) },
        { label: "Material Type", value: materialTypeName },
        { label: "Serial Number", value: serialNumber },
        { label: "Install Date", value: formatDateWithZ(installDate) }
    ];
    
    const handleCheck = (e) => {
        const isChecked = e.target.checked;
        const assetsClone = getAssetsClone(assets);
        assetsClone[index].isChecked = isChecked;
        
        // if asset is unchecked (isChecked = false), delete 3 fields below
        if (!isChecked) {
            const { isChecked, isIncludedInLeakInspection, workItemDetail, ...rest } = assetsClone[index];
            assetsClone[index] = { ...rest, workItemDetail: {} };
        }
        
        setAssets(assetsClone);
    };
    
    const handleLeakFound = (leakFound) => {
        const assetsClone = getAssetsClone(assets);
        assetsClone[index] = {
            ...assetsClone[index],
            isIncludedInLeakInspection: true,
            workItemDetail: {
                wasLeakFound: leakFound,
                // WasLeakFound = Yes & topLevelInspectionDate has value, set inspectionDate to topLevelInspectionDate
                ...(leakFound && topLevelInspectionDate && { inspectionDate: topLevelInspectionDate }),
            }
        };
        setAssets(assetsClone);
    };
    
    const setAssetField = (field, value) => {
        const assetsClone = getAssetsClone(assets);
        assetsClone[index]["workItemDetail"][field] = value;
        // user is clearing a field, remove key from state
        if (!value) {
            const { [field]: removedKey, ...rest } = assetsClone[index]["workItemDetail"];
            assetsClone[index]["workItemDetail"] = { ...rest };
        }
        
        setAssets(assetsClone);
    };

    return (
        <Grid container sx={{ ml: { xs: 0, md: 2 }, px: { xs: 2, md: 0 } }}>
            <Grid item xs={"auto"}>
                <CheckBox
                    checked={isChecked}
                    onChange={handleCheck}
                    disabled={disabled || loading}
                    labelStyle={{ mr: 0 }}
                />
            </Grid>

            <Grid item xs>
                <Paper
                    variant="outlined"
                    sx={{
                        ...paperResponsiveStyle,
                        ...((disabled || loading) && { backgroundColor: "#f5f5f5", opacity: 0.7 })
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    pr: 2,        // right padding inside Paper (for Chip)
                                    pt: 1.5,      // space from top
                                    pb: 0,        // remove space before next rows
                                }}
                            >
                                <Typography variant="body1" color={Grey1} sx={{ fontSize: "18px", fontWeight: "bold" }}>
                                    {name}
                                </Typography>
                                <Chip
                                    label={chipLabel}
                                    sx={{ color: "#FFF", backgroundColor: chipColor, mr: { xs: 0, md: 2 } }}
                                />
                            </Box>
                        </Grid>
                        {inventoryData.map(({ label, value }, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        {label.toUpperCase()}:
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {value}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <Divider component="div" />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl required sx={{ fontSize: "14px" }} disabled={disabled || !isChecked}>
                                <FormLabel sx={{ fontSize: "14px", mb: 1 }}>Was Leak Found?</FormLabel>
                                <RadioGroup
                                    row
                                    onChange={e => handleLeakFound(e.target.value === "true")}
                                    value={wasLeakFound}
                                    aria-labelledby="row-radio-buttons-group-label"
                                    sx={{ ml: 2 }}
                                >
                                    <FormControlLabel value={"true"} control={<Radio />} label="Yes" />
                                    <FormControlLabel value={"false"} control={<Radio />} label="No" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        {wasLeakFound && (
                            <>
                                <Grid item xs={12} md={4}>
                                    <DateInput
                                        variant="outlined"
                                        allowPast={true}
                                        allowFuture={false}
                                        label="Date Leak Found*"
                                        date={inspectionDate}
                                        onChange={new_date => setAssetField("inspectionDate", new_date)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Select
                                        required
                                        variant="outlined"
                                        label="Leak Location"
                                        value={leakLocationId}
                                        onChange={e => setAssetField("leakLocationId", e.target.value)}
                                        options={dropDownOptions.leakLocationCategories}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Select
                                        required
                                        variant="outlined"
                                        label="Leak Sub-Location"
                                        value={leakSublocationId}
                                        disabled={!leakLocationId}
                                        onChange={e => setAssetField("leakSublocationId", e.target.value)}
                                        options={dropDownOptions.leakLocations.filter(x => x.category === leakLocationId)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <SearchableDropdown
                                        required
                                        variant="outlined"
                                        label="Leak Cause"
                                        value={faultCauseTypeId}
                                        onChange={id => setAssetField("faultCauseTypeId", id)}
                                        options={dropDownOptions.leakCauses}
                                        searchFields={['name']}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputNumber
                                        fullWidth
                                        hideStepper
                                        label="Estimated Leak Amount"
                                        variant="outlined"
                                        value={estimatedLeakAmount}
                                        onChange={e => setAssetField("estimatedLeakAmount", e.target.value)}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default SystemSelect;